import { useAuth } from '../../contexts/AuthContext';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import CompanyInvoicesSection from '../../components/dashboard/CompanyInvoicesSection';
import NotificationsInbox from '../../components/dashboard/NotificationsInbox';
import {
  ThumbsUp, ThumbsDown, AlertCircle,
  TrendingUp, Euro, FileSignature, CalendarDays, ChevronRight,
  Archive, AlertTriangle, Sparkles, X, Check, Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';

const QUICK_RANGES = [
  { id: 'today', label: 'Heute', days: 0 },
  { id: '7d',    label: '7 Tage', days: 7 },
  { id: '30d',   label: '30 Tage', days: 30 },
  { id: '90d',   label: '90 Tage', days: 90 },
  { id: 'year',  label: 'Jahr', days: 365 },
  { id: 'custom', label: 'Benutzerdefiniert', days: null },
];

function rangeToDates(rangeId, custom) {
  const now = new Date();
  // Lokales Datum (NICHT UTC). Verhindert dass z. B. in Berlin um Mitternacht
  // der API der „falsche" Tag mitgegeben wird.
  const toIso = (d) => {
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  if (rangeId === 'custom') {
    return { from: custom.from, to: custom.to };
  }
  const r = QUICK_RANGES.find(q => q.id === rangeId);
  const days = r?.days ?? 30;
  const to = new Date(now); to.setHours(23, 59, 59, 999);
  const from = new Date(now); from.setDate(from.getDate() - days); from.setHours(0, 0, 0, 0);
  return { from: toIso(from), to: toIso(to) };
}

function bestGranularity(from, to) {
  const days = Math.max(1, (new Date(to) - new Date(from)) / 86400000);
  if (days <= 14) return 'day';
  if (days <= 90) return 'week';
  return 'month';
}

function fmtEuro(n) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(n || 0);
}

export default function DashboardHome() {
  const { user } = useAuth();
  const canSeeStats = ['admin', 'vertrieb', 'innendienst'].includes(user?.role);
  return (
    <div className="p-6 max-w-7xl">
      <h1 className="text-2xl font-bold text-[#32373c] mb-1" data-testid="dashboard-title">Willkommen, {user?.name}</h1>
      <p className="text-sm text-gray-500 mb-6">EES Internes Dashboard</p>

      {/* 27.08.2026: Prio-Reihenfolge angepasst — Benachrichtigungen und
          „Meine Termine" ganz oben (User-Spec), danach Low-Stock, Stats,
          Feedback. */}
      <NotificationsInbox />

      <CalendarUpcomingWidget />

      <LowStockWidget />

      {canSeeStats && <StatsPanel />}

      {['admin', 'support', 'technik', 'innendienst'].includes(user?.role) && <HansFeedbackWidget />}
    </div>
  );
}

function LowStockWidget() {
  const { user } = useAuth();
  const canAck = ['admin', 'team', 'innendienst', 'lager', 'technik', 'produktion'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(false);
  const [acking, setAcking] = useState('');
  const load = useCallback(async () => {
    const r = await authFetch('/api/inventory/low-stock');
    if (r.ok) { const d = await r.json(); setItems(d.items || []); }
    else setErr(true);
  }, []);
  useEffect(() => { load(); }, [load]);
  const ack = async (id) => {
    setAcking(id);
    const r = await authFetch(`/api/inventory/${id}/low-stock/ack`, { method: 'POST' });
    if (r.ok) setItems(prev => prev.filter(i => i.id !== id));
    setAcking('');
  };
  if (err || items.length === 0) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6" data-testid="low-stock-widget">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Mindestbestand unterschritten ({items.length})
        </h2>
        <Link to="/dashboard/lager" className="text-xs text-red-600 hover:underline flex items-center gap-1" data-testid="link-to-inventory">
          Zum Lager <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="low-stock-items">
        {items.slice(0, 12).map(i => (
          <span key={i.id} className="inline-flex items-center gap-1.5 text-[11px] bg-white border border-red-200 rounded-full pl-2.5 pr-1 py-1 text-red-700" data-testid={`low-stock-item-${i.id}`}>
            <Archive className="w-3 h-3" />
            {i.hersteller ? `${i.hersteller} · ` : ''}{i.modell}
            <span className="tabular-nums font-semibold">{i.stock} / {i.minStock}</span>
            {canAck && (
              <button
                onClick={() => ack(i.id)}
                disabled={acking === i.id}
                title="Warnung bestätigen — erscheint erneut, wenn der Bestand wieder über das Minimum steigt und erneut darunter fällt"
                className="ml-0.5 w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-red-100 text-red-400 hover:text-red-600 disabled:opacity-40"
                data-testid={`low-stock-ack-${i.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
        {items.length > 12 && <span className="text-[11px] text-red-600 self-center">+ {items.length - 12} weitere</span>}
      </div>
    </div>
  );
}

function StatsPanel() {
  const { user } = useAuth();
  const canSeeCompanyInvoices = ['admin', 'innendienst', 'team'].includes(user?.role);
  const [kind, setKind] = useState('orders');          // orders | invoices | both
  const [rangeId, setRangeId] = useState('30d');
  // Lokales Datum-Default — NICHT toISOString() (würde in CEST den Vortag liefern).
  const _localIso = (d) => {
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const [custom, setCustom] = useState({
    from: _localIso(new Date(Date.now() - 30 * 86400000)),
    to: _localIso(new Date()),
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const { from, to } = useMemo(() => rangeToDates(rangeId, custom), [rangeId, custom]);
  const granularity = useMemo(() => bestGranularity(from, to), [from, to]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    const params = new URLSearchParams({ from, to, kind, granularity });
    const r = await authFetch(`/api/dashboard/stats?${params}`);
    if (r.ok) setData(await r.json());
    else setErr('Statistik konnte nicht geladen werden');
    setLoading(false);
  }, [from, to, kind, granularity]);

  useEffect(() => { load(); }, [load]);

  const metric = kind === 'invoices' ? data?.invoices : data?.orders;
  const altMetric = kind === 'both' ? data?.invoices : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="stats-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#9ec043]/10 text-[#9ec043] flex items-center justify-center">
            <TrendingUp className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[#32373c]">Unternehmensauswertung</h2>
            <p className="text-[11px] text-gray-400">Aufträge & Rechnungen aus Easybill (importiert)</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Kind tabs */}
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50" data-testid="stats-kind-tabs">
            {[
              { id: 'orders', label: 'Aufträge' },
              { id: 'invoices', label: 'Rechnungen' },
              { id: 'both', label: 'Beides' },
            ].map(t => (
              <button key={t.id} onClick={() => setKind(t.id)}
                className={`text-xs px-3 py-1 rounded ${kind === t.id ? 'bg-white shadow-sm text-[#32373c] font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`stats-tab-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Range filters */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {QUICK_RANGES.map(q => (
          <button key={q.id} onClick={() => setRangeId(q.id)}
            className={`text-xs px-2.5 py-1 rounded-md border ${rangeId === q.id ? 'bg-[#9ec043] border-[#9ec043] text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            data-testid={`stats-range-${q.id}`}>
            {q.label}
          </button>
        ))}
        {rangeId === 'custom' && (
          <div className="flex items-center gap-1 text-xs" data-testid="stats-custom-range">
            <input type="date" value={custom.from} onChange={e => setCustom(p => ({ ...p, from: e.target.value }))}
              className="border border-gray-200 rounded px-2 py-1 text-xs" data-testid="stats-from" />
            <span className="text-gray-400">→</span>
            <input type="date" value={custom.to} onChange={e => setCustom(p => ({ ...p, to: e.target.value }))}
              className="border border-gray-200 rounded px-2 py-1 text-xs" data-testid="stats-to" />
          </div>
        )}
        <span className="text-[10px] text-gray-400 ml-auto">Zeitraum: {from} → {to} · {granularity === 'day' ? 'täglich' : granularity === 'week' ? 'wöchentlich' : 'monatlich'}</span>
      </div>

      {err && <div className="text-xs text-red-500 mb-3">{err}</div>}
      {loading && <div className="text-xs text-gray-400">Lade…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <KpiCard icon={FileSignature} label={kind === 'invoices' ? 'Rechnungen' : 'Aufträge'}
              value={metric?.count ?? 0} testid="kpi-count" />
            <KpiCard icon={Euro} label={kind === 'invoices' ? 'Rechnungssumme' : 'Auftragsvolumen'}
              value={fmtEuro(metric?.sum)} testid="kpi-sum" />
            <KpiCard icon={TrendingUp} label="Ø Wert"
              value={fmtEuro(metric?.avg)} testid="kpi-avg" />
            {kind === 'both'
              ? <KpiCard icon={Euro} label="Rechnungssumme" value={fmtEuro(altMetric?.sum)} testid="kpi-sum2" />
              : <KpiCard icon={CalendarDays} label="Dokumente gesamt" value={data.totalDocs} testid="kpi-total" />
            }
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border border-gray-100 rounded-lg p-3 bg-gray-50/40">
              <h3 className="text-xs font-semibold text-[#32373c] mb-2">Entwicklung über Zeit</h3>
              <div className="h-56" data-testid="stats-chart">
                <ResponsiveContainer width="100%" height="100%">
                  {kind === 'both' ? (
                    <LineChart data={data.series} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                      <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => fmtEuro(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="orders" name="Aufträge" stroke="#9ec043" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="invoices" name="Rechnungen" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  ) : (
                    <BarChart data={data.series} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                      <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => fmtEuro(v)} />
                      <Bar dataKey={kind === 'invoices' ? 'invoices' : 'orders'}
                        name={kind === 'invoices' ? 'Rechnungen' : 'Aufträge'}
                        fill={kind === 'invoices' ? '#3b82f6' : '#9ec043'} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-gray-100 rounded-lg p-3 bg-gray-50/40">
              <h3 className="text-xs font-semibold text-[#32373c] mb-2">
                {kind === 'invoices' ? 'Status (Easybill)' : 'Status-Verteilung'}
              </h3>
              <StatusPie data={kind === 'invoices' ? metric?.easybillStatusCounts : metric?.statusCounts} />
            </div>
          </div>

          {data.topCustomers?.length > 0 && (
            <div className="mt-5">
              <h3 className="text-xs font-semibold text-[#32373c] mb-2">Top-Kunden im Zeitraum</h3>
              <div className="space-y-1" data-testid="stats-top-customers">
                {data.topCustomers.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2.5 py-1.5 border border-gray-100">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-5 h-5 rounded-full bg-[#9ec043]/10 text-[#9ec043] text-[10px] font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="text-gray-700 truncate">{c.name}</span>
                      <span className="text-[10px] text-gray-400 shrink-0">· {c.count} Doks</span>
                    </span>
                    <span className="text-[#32373c] font-medium shrink-0">{fmtEuro(c.sum)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Einkauf-Abschnitt: separate Auswertung der Lager-Rechnungen */}
          {data.purchases?.count > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-5" data-testid="stats-purchases">
              <h3 className="text-sm font-semibold text-[#32373c] mb-3">
                Einkauf (Lager-Rechnungen) im Zeitraum
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                <KpiCard icon={FileSignature} label="Anzahl Rechnungen" value={data.purchases.count} testid="kpi-purchase-count" />
                <KpiCard icon={Euro} label="Einkaufsvolumen" value={fmtEuro(data.purchases.sum)} testid="kpi-purchase-sum" />
                <KpiCard icon={TrendingUp} label="Ø Rechnungswert" value={fmtEuro(data.purchases.avg)} testid="kpi-purchase-avg" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-gray-100 rounded-lg p-3 bg-amber-50/40">
                  <h4 className="text-xs font-semibold text-[#32373c] mb-2">Monatlicher Einkauf</h4>
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.series} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                        <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => fmtEuro(v)} />
                        <Bar dataKey="purchases" name="Einkauf" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                {data.purchases.topSuppliers?.length > 0 && (
                  <div className="border border-gray-100 rounded-lg p-3 bg-amber-50/40">
                    <h4 className="text-xs font-semibold text-[#32373c] mb-2">Top-Lieferanten</h4>
                    <div className="space-y-1">
                      {data.purchases.topSuppliers.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border border-amber-100">
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                            <span className="text-gray-700 truncate">{s.name}</span>
                            <span className="text-[10px] text-gray-400 shrink-0">· {s.count}</span>
                          </span>
                          <span className="text-[#32373c] font-medium shrink-0">{fmtEuro(s.sum)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {metric?.count === 0 && altMetric?.count === undefined && (
            <div className="text-xs text-gray-400 italic mt-4">
              Keine Daten für diesen Zeitraum. Importiere Aufträge/Rechnungen aus Easybill im <Link to="/dashboard/auftraege" className="text-[#9ec043] underline">Aufträge-Kanban</Link>.
            </div>
          )}

          {canSeeCompanyInvoices && <CompanyInvoicesSection />}
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-white" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-400">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-xl font-bold text-[#32373c] mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

const STATUS_COLORS = ['#9ec043', '#3b82f6', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4', '#64748b'];

function StatusPie({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) {
    return <div className="h-40 flex items-center justify-center text-[11px] text-gray-400">Keine Daten</div>;
  }
  const chartData = entries.map(([k, v]) => ({ name: k, value: v }));
  return (
    <div className="h-40" data-testid="stats-status-pie">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={25} outerRadius={55} paddingAngle={2}>
            {chartData.map((_, i) => <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
          </Pie>
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 10 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function ReminderEventCard({ ev, prefix }) {
  const start = new Date(ev.start);
  const color = (ev.attendeeColors && ev.attendeeColors[0])
    || ev.creatorColor
    || ev.color
    || '#9ec043';
  return (
    <div className="flex items-center gap-3 text-xs bg-gray-50 rounded px-2.5 py-2 border border-gray-100" data-testid={`reminder-${prefix}-${ev.id}`}>
      <span className="w-1 h-8 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <div className="flex-1 min-w-0">
        <div className="text-[#32373c] font-medium truncate">{ev.title}</div>
        <div className="text-[10px] text-gray-400">
          {start.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short' })}
          {' · '}
          {ev.allDay ? <span className="uppercase">Ganztägig</span>
            : start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
          {ev.location && <> · {ev.location}</>}
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-wider text-gray-400 shrink-0">{ev.category}</span>
    </div>
  );
}

function ReminderSection({ title, items, tone, testid }) {
  return (
    <div className="mb-3 last:mb-0" data-testid={testid}>
      <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${tone}`}>{title} ({items.length})</div>
      {items.length === 0 ? (
        <div className="text-[11px] text-gray-300 italic">Keine Termine</div>
      ) : (
        <div className="space-y-1.5">{items.map(ev => <ReminderEventCard key={ev.id} ev={ev} prefix={testid} />)}</div>
      )}
    </div>
  );
}

function CalendarUpcomingWidget() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      const now = new Date();
      const in14d = new Date(now.getTime() + 14 * 86400000);
      const pad = n => String(n).padStart(2, '0');
      const localIso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const params = new URLSearchParams({ from: localIso(now), to: localIso(in14d), mine: 'true' });
      const r = await authFetch(`/api/calendar/events?${params}`);
      if (r.ok) {
        const d = await r.json();
        setEvents(d.events || []);
      } else setErr(true);
    })();
  }, []);

  // „Meine Termine"-Semantik: nur wo ich WIRKLICH dabei bin.
  // - Termine mit Attendees: ich muss in der Liste stehen
  // - Termine ohne Attendees (persönliche Erinnerungen): nur für den Ersteller
  // Termine, die ich für ANDERE angelegt habe (ohne mich selbst als
  // Attendee), zählen NICHT mehr zu „Meine Termine".
  const myEvents = events.filter(ev => {
    if (!user?.id) return false;
    const attendees = Array.isArray(ev.attendees) ? ev.attendees : [];
    if (attendees.length > 0) {
      return attendees.includes(user.id);
    }
    // keine Attendees → persönlicher Termin → nur Ersteller sieht ihn
    return ev.createdBy === user.id;
  });

  const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const today = startOfDay(new Date()).getTime();
  const tomorrow = today + 86400000;
  const dayAfter = today + 2 * 86400000;
  const inOneWeek = today + 7 * 86400000;

  const heute = myEvents.filter(ev => {
    const s = startOfDay(new Date(ev.start)).getTime();
    const e = startOfDay(new Date(ev.end || ev.start)).getTime();
    return s <= today && e >= today;
  });
  const morgen = myEvents.filter(ev => {
    const s = startOfDay(new Date(ev.start)).getTime();
    const e = startOfDay(new Date(ev.end || ev.start)).getTime();
    return s <= tomorrow && e >= tomorrow && !(s <= today && e >= today);
  });
  const dieseWoche = myEvents.filter(ev => {
    const s = startOfDay(new Date(ev.start)).getTime();
    return s >= dayAfter && s <= inOneWeek;
  }).slice(0, 5);

  if (err) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="upcoming-events-widget">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#9ec043]" /> Meine Termine
        </h2>
        <Link to="/dashboard/kalender" className="text-xs text-[#9ec043] hover:underline flex items-center gap-1" data-testid="link-to-calendar">
          Kalender öffnen <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      {myEvents.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Keine anstehenden Termine in den nächsten 14 Tagen.</p>
      ) : (
        <>
          <ReminderSection title="Heute" items={heute} tone="text-red-600" testid="reminders-today" />
          <ReminderSection title="Morgen" items={morgen} tone="text-amber-600" testid="reminders-tomorrow" />
          {dieseWoche.length > 0 && (
            <ReminderSection title="Diese Woche" items={dieseWoche} tone="text-[#9ec043]" testid="reminders-week" />
          )}
        </>
      )}
    </div>
  );
}

function HansFeedbackWidget() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [improvingItem, setImprovingItem] = useState(null);  // Modal-Steuerung

  const load = useCallback(async () => {
    const res = await authFetch('/api/chat/feedback/summary');
    if (res.ok) setData(await res.json());
    else setErr('Konnte Feedback nicht laden');
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return null;
  if (!data) return <div className="text-xs text-gray-400" data-testid="hans-feedback-loading">Lade Frag-HANS-Feedback…</div>;

  const total = data.totalUp + data.totalDown;
  const positive = total ? Math.round((data.totalUp / total) * 100) : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="hans-feedback-widget">
      <h2 className="text-sm font-semibold text-[#32373c] mb-3 flex items-center gap-2">
        <ThumbsUp className="w-4 h-4 text-[#9ec043]" /> Frag-HANS-Nutzerfeedback
      </h2>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <div className="text-2xl font-bold text-green-600 flex items-center gap-1">
            <ThumbsUp className="w-5 h-5" /> {data.totalUp}
          </div>
          <div className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">hilfreich</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-red-600 flex items-center gap-1">
            <ThumbsDown className="w-5 h-5" /> {data.totalDown}
          </div>
          <div className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">nicht hilfreich</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-[#32373c]">{positive}%</div>
          <div className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">Zustimmung</div>
        </div>
      </div>
      {data.worstCitations?.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-[#32373c] mb-1.5 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 text-amber-500" /> Handbuch-Passagen mit niedriger Bewertung
          </h3>
          <div className="space-y-1" data-testid="hans-worst-citations">
            {data.worstCitations.map((w, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-amber-50/50 rounded px-2 py-1 border border-amber-100">
                <span className="text-gray-700 truncate flex-1">{w.title || w.docId?.slice(0,8) || '—'}</span>
                <span className="text-red-600 font-medium shrink-0 ml-2">👎 {w.downvotes}×</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {(data.recentDownvotes?.length > 0 || data.recentUpvotes?.length > 0) && (
        <FeedbackRowList
          downvotes={data.recentDownvotes || []}
          upvotes={data.recentUpvotes || []}
          onImprove={setImprovingItem}
          onDeleted={load}
        />
      )}
      {improvingItem && (
        <ImproveAnswerModal
          item={improvingItem}
          onClose={() => setImprovingItem(null)}
          onSaved={() => { setImprovingItem(null); load(); }}
        />
      )}
      {total === 0 && (
        <p className="text-xs text-gray-400 italic">
          Noch kein Feedback eingegangen. Sobald Nutzer auf /frag-hans Daumen vergeben, erscheinen hier Statistiken und eure schwächsten Handbuch-Passagen.
        </p>
      )}
    </div>
  );
}


// =============================================================
// Eintragsliste: Positive UND negative Frag-Hans-Bewertungen
// =============================================================
function FeedbackRowList({ downvotes, upvotes, onImprove, onDeleted }) {
  const [busyKey, setBusyKey] = useState(null);

  const remove = async (item) => {
    const key = `${item.sessionId}:${item.messageId}`;
    if (!window.confirm('Dieses Feedback wirklich löschen? (z. B. wenn es nur ein Test-Eintrag war)')) return;
    setBusyKey(key);
    try {
      const res = await authFetch(
        `/api/chat/feedback/${encodeURIComponent(item.sessionId)}/${encodeURIComponent(item.messageId)}`,
        { method: 'DELETE' },
      );
      if (res.ok) onDeleted?.();
    } finally {
      setBusyKey(null);
    }
  };

  const renderRow = (d, i, tone) => {
    const key = `${d.sessionId}:${d.messageId}`;
    const dt = d.updatedAt ? new Date(d.updatedAt) : null;
    const dateStr = dt ? dt.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }) : '';
    const who = d.customerName || d.customerEmail || 'anonym';
    const cls = tone === 'up'
      ? 'bg-emerald-50/40 border-emerald-100'
      : 'bg-red-50/40 border-red-100';
    return (
      <div key={key} className={`text-xs border rounded-md p-2 space-y-1 ${cls}`}
           data-testid={`hans-${tone === 'up' ? 'upvote' : 'downvote'}-${i}`}>
        <div className="flex items-center justify-between gap-2 text-[10px] text-gray-500">
          <span className="font-medium text-gray-700">
            {who}
            {d.customerEmail && d.customerName && (
              <span className="font-normal text-gray-400"> · {d.customerEmail}</span>
            )}
          </span>
          <span>{dateStr}</span>
        </div>
        {d.questionPreview && (
          <div className="text-gray-700">
            <span className="text-[10px] font-semibold text-gray-500 uppercase">Frage: </span>
            {d.questionPreview}
          </div>
        )}
        {d.answerPreview && (
          <div className="text-gray-600 italic">
            <span className="text-[10px] font-semibold text-gray-500 uppercase not-italic">HANS: </span>
            {d.answerPreview}
          </div>
        )}
        {d.comment && (
          <div className={tone === 'up'
            ? 'text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5'
            : 'text-amber-700 bg-amber-50 rounded px-1.5 py-0.5'}>
            <span className="text-[10px] font-semibold uppercase">Kommentar: </span>
            {d.comment}
          </div>
        )}
        {Array.isArray(d.citations) && d.citations.length > 0 && (
          <div className="text-[10px] text-gray-400">
            Quellen: {d.citations.map(c => c.title || c.docId?.slice(0, 8)).filter(Boolean).join(' · ')}
          </div>
        )}
        <div className="flex justify-end pt-1 gap-1.5">
          {tone === 'down' && (
            <button
              onClick={() => onImprove(d)}
              className="text-[10px] font-semibold text-[#9ec043] hover:text-[#7a9c34] flex items-center gap-1 px-2 py-1 rounded border border-[#9ec043]/30 hover:bg-[#9ec043]/5 transition-colors"
              data-testid={`btn-improve-${i}`}
            >
              <Sparkles className="w-3 h-3" /> Antwort verbessern
            </button>
          )}
          <button
            onClick={() => remove(d)}
            disabled={busyKey === key}
            className="text-[10px] font-semibold text-red-600 hover:text-red-700 flex items-center gap-1 px-2 py-1 rounded border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-40"
            data-testid={`btn-delete-fb-${tone}-${i}`}
            title="Test-Eintrag oder Fehleintrag löschen"
          >
            <Trash2 className="w-3 h-3" /> Löschen
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {downvotes.length > 0 && (
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-[#32373c] mb-1.5 flex items-center gap-1">
            <ThumbsDown className="w-3.5 h-3.5 text-red-500" /> Letzte negative Bewertungen
            <span className="text-gray-400 font-normal ml-1">({downvotes.length})</span>
          </h3>
          <div className="space-y-2" data-testid="hans-recent-downvotes">
            {downvotes.map((d, i) => renderRow(d, i, 'down'))}
          </div>
        </div>
      )}
      {upvotes.length > 0 && (
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-[#32373c] mb-1.5 flex items-center gap-1">
            <ThumbsUp className="w-3.5 h-3.5 text-emerald-600" /> Letzte positive Bewertungen
            <span className="text-gray-400 font-normal ml-1">({upvotes.length})</span>
          </h3>
          <div className="space-y-2" data-testid="hans-recent-upvotes">
            {upvotes.map((d, i) => renderRow(d, i, 'up'))}
          </div>
        </div>
      )}
    </>
  );
}


// =============================================================
// Modal: korrigierte HANS-Antwort als Knowledge-Base-Eintrag speichern
// =============================================================
const KB_CATEGORIES = [
  { id: 'faq',      label: 'FAQ / Häufige Frage' },
  { id: 'produkt',  label: 'Produkt-Info' },
  { id: 'technik',  label: 'Technische Details' },
  { id: 'usp',      label: 'USP / Alleinstellungsmerkmal' },
  { id: 'fehler',   label: 'Fehler / Troubleshooting' },
  { id: 'montage',  label: 'Montage-Hinweise' },
  { id: 'support',  label: 'Support-Hinweise' },
  { id: 'vertrieb', label: 'Vertriebs-Info' },
];

function ImproveAnswerModal({ item, onClose, onSaved }) {
  // Titel-Vorschlag aus der Frage extrahieren — max. 80 Zeichen, ohne
  // Newlines.
  const initialTitle = useMemo(() => {
    const q = (item?.questionPreview || '').replace(/\s+/g, ' ').trim();
    return q.length > 80 ? q.slice(0, 77) + '…' : q;
  }, [item]);
  const [title, setTitle] = useState(initialTitle);
  const [category, setCategory] = useState('faq');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    if (!title.trim() || content.trim().length < 20) {
      setError('Bitte Titel ausfüllen und mindestens 20 Zeichen Inhalt eingeben.');
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch('/api/chat/feedback/improve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: item.sessionId,
          messageId: item.messageId,
          title: title.trim(),
          category,
          content: content.trim(),
          question: item.questionPreview,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Speichern fehlgeschlagen');
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="improve-answer-modal">
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-[#32373c] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#9ec043]" /> HANS-Antwort verbessern
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 rounded"
                  data-testid="btn-close-improve">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-xs">
            <div>
              <span className="text-[10px] font-semibold text-gray-500 uppercase">Ursprüngliche Frage</span>
              <p className="text-gray-700 mt-0.5">{item.questionPreview || <em className="text-gray-400">(keine Frage gespeichert)</em>}</p>
            </div>
            {item.answerPreview && (
              <div>
                <span className="text-[10px] font-semibold text-gray-500 uppercase">Was HANS antwortete</span>
                <p className="text-gray-600 italic mt-0.5">{item.answerPreview}</p>
              </div>
            )}
            {item.comment && (
              <div>
                <span className="text-[10px] font-semibold text-amber-700 uppercase">Nutzer-Kommentar</span>
                <p className="text-amber-700 mt-0.5">{item.comment}</p>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-gray-500 font-semibold">Titel des Knowledge-Eintrags</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="z. B. „Kann HS 6 PV als Notstrom genutzt werden?"
                   className="mt-1 w-full h-9 rounded-md border border-gray-300 px-2 text-sm focus:outline-none focus:border-[#9ec043]"
                   data-testid="improve-title-input" />
          </div>

          <div>
            <label className="text-xs text-gray-500 font-semibold">Kategorie</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
                    className="mt-1 w-full h-9 rounded-md border border-gray-300 px-2 text-sm bg-white"
                    data-testid="improve-category-select">
              {KB_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-500 font-semibold flex items-center gap-2">
              Korrekte Antwort (für HANS)
              <span className="text-[10px] text-gray-400">— wird priorisiert bei zukünftigen Anfragen verwendet</span>
            </label>
            <textarea value={content} onChange={(e) => setContent(e.target.value)}
                      placeholder="Tippe hier die korrekte Antwort/Erklärung. HANS wird sie in Zukunft bevorzugen, wenn Nutzer ähnliche Fragen stellen."
                      rows={8}
                      className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:border-[#9ec043] resize-y"
                      data-testid="improve-content-textarea" />
            <p className="text-[10px] text-gray-400 mt-1">
              {content.trim().length} / mindestens 20 Zeichen
            </p>
          </div>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 bg-gray-50">
          <button onClick={onClose} disabled={saving}
                  className="px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 rounded"
                  data-testid="btn-improve-cancel">
            Abbrechen
          </button>
          <button onClick={save} disabled={saving}
                  className="px-3 py-1.5 text-xs font-semibold bg-[#9ec043] text-white rounded hover:bg-[#7a9c34] disabled:opacity-50 flex items-center gap-1"
                  data-testid="btn-improve-save">
            <Check className="w-3 h-3" /> {saving ? 'Speichert…' : 'Als HANS-Wissen speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
